/* eslint-disable max-classes-per-file */

import { formatISO } from 'date-fns'

import { uuid } from '../../../utils'
import { StringOrNull } from '../../../constants'

import { TelemetryEvent } from '../types'
import { VIDEO_PLAYBACK_TYPE } from './enums'
import { EVENT_PLAYBACK_STATUS } from '../enums'


export type Chapter = {
  id: StringOrNull
  marker: number
  title: string
}

type BaseData = {
  reason?: EVENT_PLAYBACK_STATUS | null
  status?: EVENT_PLAYBACK_STATUS
  requestStatus?: string
}

export type VideoData<T> = {
  currentTimestamp: number
  maxTimestamp: number
  progress: number
  duration: number
  status?: EVENT_PLAYBACK_STATUS
  reason?: EVENT_PLAYBACK_STATUS | null
  chapter?: T
}

export type ChapterProgressData = {
  currentTimestamp: number
  maxTimestamp: number
  duration: number
  progress: number
} & Chapter

type Data = {
  chapter?: Chapter
}

type RangeStartData = {
  currentTimestamp: number
}

type RangeEndData = {
  startTimestamp: number
  endTimestamp: number
  duration: number
  reason: EVENT_PLAYBACK_STATUS
}

export abstract class VideoPlaybackTelemetryEvent<T> implements TelemetryEvent<T> {
  id: string
  time: string
  type!: VIDEO_PLAYBACK_TYPE
  data: T & BaseData

  set reason(value: EVENT_PLAYBACK_STATUS) {
    if (!this.data) return
    this.data.reason = value
  }

  set status(value: EVENT_PLAYBACK_STATUS) {
    if (!this.data) return
    this.data.reason = value
  }

  constructor() {
    this.id = uuid()
    this.time = formatISO(new Date)
    this.data = Object.create(null) as T & BaseData
  }

  toString() {
    return JSON.stringify({
      id: this.id,
      time: this.time,
      type: this.type,
      data: this.data
    })
  }
}

export class BaseVideoTelemetryEvent<T> extends VideoPlaybackTelemetryEvent<T> {
  constructor(id: string, type: VIDEO_PLAYBACK_TYPE, time: string, data: T) {
    super()

    this.id = id
    this.type = type
    this.time = time

    if (data) this.data = data
  }

  static load(event: string) {
    const e = JSON.parse(event) as TelemetryEvent<unknown>
    return new BaseVideoTelemetryEvent(e.id, e.type as never, e.time, e.data)
  }
}

export class VideoStartedTelemetryEvent extends VideoPlaybackTelemetryEvent<Data> {
  type = VIDEO_PLAYBACK_TYPE.STARTED
}
export class VideoProgressedTelemetryEvent extends VideoPlaybackTelemetryEvent<VideoData<Chapter>> {
  type = VIDEO_PLAYBACK_TYPE.PROGRESSED

  constructor(
    position: number,
    maxPosition: number,
    progress: number,
    duration: number,
    status: EVENT_PLAYBACK_STATUS,
    chapter?: Chapter
  ) {
    super()

    this.data = {
      currentTimestamp: position,
      maxTimestamp: maxPosition,
      progress,
      duration,
      status,
      chapter
    }
  }
}

export class VideoEndedTelemetryEvent extends VideoProgressedTelemetryEvent {
  type = VIDEO_PLAYBACK_TYPE.ENDED

  constructor(
    position: number,
    maxPosition: number,
    progress: number,
    duration: number,
    reason: EVENT_PLAYBACK_STATUS,
    chapter?: Chapter
  ) {
    super(position, maxPosition, progress, duration, undefined as unknown as EVENT_PLAYBACK_STATUS, chapter)
    delete this.data.status
    this.data.reason = reason
  }
}

export class VideoChapterStartedTelemetryEvent extends VideoStartedTelemetryEvent {
  type = VIDEO_PLAYBACK_TYPE.CHAPTER_STARTED

  constructor(chapter: Chapter) {
    super()

    this.data = {
      chapter
    }
  }
}

export class VideoChapterEndedTelemetryEvent extends VideoPlaybackTelemetryEvent<{reason: EVENT_PLAYBACK_STATUS, chapter: ChapterProgressData}> {
  type = VIDEO_PLAYBACK_TYPE.CHAPTER_ENDED

  constructor(position: number, maxPosition: number, duration: number, progress: number, chapter: Chapter, reason: EVENT_PLAYBACK_STATUS) {
    super()

    this.data = {
      reason,
      chapter: {
        ...chapter,
        currentTimestamp: position,
        maxTimestamp: maxPosition,
        duration,
        progress
      }
    }
  }
}

export class VideoRangeStartedTelemetryEvent extends VideoPlaybackTelemetryEvent<RangeStartData> {
  type = VIDEO_PLAYBACK_TYPE.RANGE_STARTED

  constructor(start: number) {
    super()

    this.data = {
      currentTimestamp: start
    }
  }
}

export class VideoRangeEndedTelemetryEvent extends VideoPlaybackTelemetryEvent<RangeEndData> {
  type = VIDEO_PLAYBACK_TYPE.RANGE_ENDED

  constructor(start: number, end: number, duration: number, reason: EVENT_PLAYBACK_STATUS) {
    super()

    this.data = {
      startTimestamp: start,
      endTimestamp: end,
      duration,
      reason
    }
  }
}
